import { useEffect, useMemo, useState } from "react";
import type { KnowledgeEdge, KnowledgeNode } from "./KnowledgeWorkbench";

interface GraphViewProps {
  kbId: string;
}

interface GraphResponse {
  nodes?: KnowledgeNode[];
  edges?: KnowledgeEdge[];
}

export function GraphView({ kbId }: GraphViewProps) {
  const [nodes, setNodes] = useState<KnowledgeNode[]>([]);
  const [edges, setEdges] = useState<KnowledgeEdge[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/knowledge/${kbId}/graph`)
      .then((response) => response.json())
      .then((data: GraphResponse) => {
        if (!cancelled) {
          setNodes(data.nodes || []);
          setEdges(data.edges || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNodes([]);
          setEdges([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [kbId]);

  const filteredNodes = useMemo(() => {
    if (!search.trim()) {
      return nodes;
    }
    const query = search.toLowerCase();
    return nodes.filter((node) => {
      return (
        node.title.toLowerCase().includes(query) ||
        node.content.toLowerCase().includes(query) ||
        node.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    });
  }, [nodes, search]);

  const selectedNode =
    filteredNodes.find((node) => node.id === selectedNodeId) ||
    nodes.find((node) => node.id === selectedNodeId) ||
    filteredNodes[0] ||
    null;

  const selectedEdges = useMemo(() => {
    if (!selectedNode) {
      return [];
    }
    return edges.filter(
      (edge) => edge.from_node === selectedNode.id || edge.to_node === selectedNode.id,
    );
  }, [edges, selectedNode]);

  return (
    <div style={S.root}>
      <div style={S.toolbar}>
        <div>
          <h3 style={S.heading}>Graph Explorer</h3>
          <p style={S.subheading}>
            {nodes.length} nodes, {edges.length} edges
          </p>
        </div>
        <input
          style={S.search}
          placeholder="Search nodes..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div style={S.stats}>
        <StatCard label="File nodes" value={countByType(nodes, "file")} />
        <StatCard label="Concept nodes" value={countByType(nodes, "concept")} />
        <StatCard label="Tagged nodes" value={nodes.filter((node) => node.tags.length > 0).length} />
        <StatCard label="Connected nodes" value={new Set(edges.flatMap((edge) => [edge.from_node, edge.to_node])).size} />
      </div>

      <div style={S.content}>
        <div style={S.listPane}>
          {loading ? (
            <div style={S.empty}>Loading graph...</div>
          ) : filteredNodes.length === 0 ? (
            <div style={S.empty}>
              No graph nodes yet. Add sources and index them, or create nodes through the API.
            </div>
          ) : (
            filteredNodes.map((node) => (
              <button
                key={node.id}
                style={{
                  ...S.nodeCard,
                  ...(selectedNode?.id === node.id ? S.nodeCardActive : {}),
                }}
                onClick={() => setSelectedNodeId(node.id)}
              >
                <div style={S.nodeTitleRow}>
                  <span style={S.nodeTitle}>{node.title}</span>
                  <span style={S.nodeType}>{node.node_type}</span>
                </div>
                <div style={S.nodePreview}>{truncate(node.content, 140)}</div>
                {node.tags.length > 0 && (
                  <div style={S.tagRow}>
                    {node.tags.map((tag) => (
                      <span key={tag} style={S.tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            ))
          )}
        </div>

        <div style={S.detailPane}>
          {selectedNode ? (
            <>
              <div style={S.detailHeader}>
                <div>
                  <div style={S.detailTitle}>{selectedNode.title}</div>
                  <div style={S.detailMeta}>
                    {selectedNode.node_type}
                    {selectedNode.source_id ? ` · source ${selectedNode.source_id}` : ""}
                  </div>
                </div>
              </div>
              <div style={S.detailSection}>
                <div style={S.sectionLabel}>Content</div>
                <div style={S.detailText}>{selectedNode.content || "No content stored."}</div>
              </div>
              <div style={S.detailSection}>
                <div style={S.sectionLabel}>Relationships</div>
                {selectedEdges.length === 0 ? (
                  <div style={S.emptyInline}>No edges connected to this node yet.</div>
                ) : (
                  selectedEdges.map((edge) => (
                    <div key={edge.id} style={S.edgeRow}>
                      <span style={S.edgeType}>{edge.edge_type}</span>
                      <span style={S.edgeLink}>
                        {edge.from_node} → {edge.to_node}
                      </span>
                      <span style={S.edgeWeight}>{edge.weight.toFixed(2)}</span>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div style={S.empty}>Select a node to inspect its content and relationships.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div style={S.statCard}>
      <div style={S.statValue}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

function countByType(nodes: KnowledgeNode[], type: string) {
  return nodes.filter((node) => node.node_type === type).length;
}

function truncate(value: string, max: number) {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

const S: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    padding: "1rem",
    height: "100%",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  },
  heading: {
    margin: 0,
    fontSize: "0.95rem",
    color: "var(--ot-text-primary, #e6edf3)",
  },
  subheading: {
    margin: "0.2rem 0 0",
    fontSize: "0.72rem",
    color: "var(--ot-text-secondary, #8b949e)",
  },
  search: {
    width: "260px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "6px",
    color: "var(--ot-text-primary, #e6edf3)",
    padding: "0.55rem 0.65rem",
    fontSize: "0.78rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "0.75rem",
  },
  statCard: {
    padding: "0.85rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  statValue: {
    fontSize: "1.15rem",
    color: "var(--ot-accent, #4f8fff)",
    fontWeight: 700,
  },
  statLabel: {
    marginTop: "0.2rem",
    fontSize: "0.68rem",
    color: "var(--ot-text-secondary, #8b949e)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  content: {
    display: "grid",
    gridTemplateColumns: "320px 1fr",
    gap: "1rem",
    minHeight: 0,
    flex: 1,
  },
  listPane: {
    display: "flex",
    flexDirection: "column",
    gap: "0.65rem",
    overflowY: "auto",
  },
  detailPane: {
    minHeight: 0,
    overflowY: "auto",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
    padding: "1rem",
  },
  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "160px",
    padding: "1rem",
    textAlign: "center",
    color: "var(--ot-text-muted, #6e7681)",
    border: "1px dashed rgba(255,255,255,0.08)",
    borderRadius: "10px",
    lineHeight: 1.6,
  },
  nodeCard: {
    textAlign: "left",
    padding: "0.85rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
    cursor: "pointer",
  },
  nodeCardActive: {
    border: "1px solid rgba(79,143,255,0.3)",
    background: "rgba(79,143,255,0.08)",
  },
  nodeTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
  },
  nodeTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "var(--ot-text-primary, #e6edf3)",
  },
  nodeType: {
    fontSize: "0.64rem",
    color: "var(--ot-cyan, #7ae8e8)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  nodePreview: {
    marginTop: "0.35rem",
    fontSize: "0.7rem",
    color: "var(--ot-text-secondary, #8b949e)",
    lineHeight: 1.5,
  },
  tagRow: {
    display: "flex",
    gap: "0.35rem",
    flexWrap: "wrap",
    marginTop: "0.5rem",
  },
  tag: {
    fontSize: "0.62rem",
    padding: "0.12rem 0.35rem",
    borderRadius: "999px",
    background: "rgba(122,232,232,0.12)",
    color: "var(--ot-cyan, #7ae8e8)",
  },
  detailHeader: {
    marginBottom: "1rem",
  },
  detailTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "var(--ot-text-primary, #e6edf3)",
  },
  detailMeta: {
    marginTop: "0.2rem",
    fontSize: "0.68rem",
    color: "var(--ot-text-secondary, #8b949e)",
  },
  detailSection: {
    marginTop: "1rem",
  },
  sectionLabel: {
    marginBottom: "0.45rem",
    fontSize: "0.68rem",
    color: "var(--ot-accent, #4f8fff)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  detailText: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    fontSize: "0.75rem",
    color: "var(--ot-text-primary, #e6edf3)",
  },
  emptyInline: {
    fontSize: "0.72rem",
    color: "var(--ot-text-muted, #6e7681)",
  },
  edgeRow: {
    display: "grid",
    gridTemplateColumns: "110px 1fr 60px",
    gap: "0.75rem",
    padding: "0.45rem 0",
    borderTop: "1px solid rgba(255,255,255,0.05)",
    fontSize: "0.72rem",
  },
  edgeType: {
    color: "var(--ot-purple, #bc8cff)",
  },
  edgeLink: {
    color: "var(--ot-text-secondary, #8b949e)",
    wordBreak: "break-all",
  },
  edgeWeight: {
    color: "var(--ot-text-primary, #e6edf3)",
    textAlign: "right",
  },
};
